let playingMediaElem: null | HTMLVideoElement | HTMLAudioElement = null;

// Function to detect if the browser is Safari
function isSafari(): boolean {
  const ua = navigator.userAgent.toLowerCase();
  return ua.includes("safari") && !ua.includes("chrome") && !ua.includes("android");
}

/* --- Video Player --- */
const playSvgButton: string = `
  <button type="button"
    class="play-audio-btn mx-auto z-[1] bg-black/70 rounded-full p-3 pl-4 transition-all hover:scale-110 size-16 group-[.is-list]:size-10 group-[.is-list]:p-1 group-[.is-list]:pl-1.5 flex justify-center items-center"
    data-audio-id=""
    aria-label="Play Video">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="48"
      height="48"
      viewBox="0 0 24 24"
      fill="white"
      stroke="white"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      class="lucide lucide-play size-10 group-[.is-list]:size-5">
      <polygon points="6 3 20 12 6 21 6 3" />
    </svg>
  </button>
`;

const tutorialPlayButton: string = `
  <button class="play-audio-btn absolute top-[50%] left-[50%] mt-[-30px] ml-[-30px] w-[60px] h-[60px] bg-black/50 rounded-full border-2 border-white backdrop-blur-md cursor-pointer flex items-center justify-center z-[2] transition-all hover:scale-110">
    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none" class="ml-1">
      <path fill-rule="evenodd" clip-rule="evenodd" d="M6 7.53882C6 5.63615 8.03867 4.43215 9.70533 5.34815L25.092 13.8108C26.8187 14.7602 26.8187 17.2415 25.092 18.1922L9.70667 26.6548C8.04 27.5708 6.00133 26.3655 6.00133 24.4642L6 7.53882Z" fill="#fff"/>
    </svg>
  </button>
`;

function initVideoPlayerSingle(videoContainer: HTMLElement, autoPlay: boolean = false, isTutorial: boolean = false) {
  const videoElem = videoContainer.querySelector("video") as HTMLVideoElement;

  if (!videoElem) {
    console.error("Can't find the video element inside video-container");
    return;
  }

  videoElem.controls = true; // Always enable default controls

  if (isSafari()) {
    // In Safari, rely on default controls and skip custom logic
    if (autoPlay) {
      videoElem.muted = false; // Mute for Safari autoplay compatibility
      // videoElem.play().catch(() => console.log("Autoplay blocked in Safari"));
    }
    return;
  }

  // Custom logic for non-Safari browsers
  const overlay = document.createElement("div");
  const playButton = document.createElement("div");

  overlay.className = "absolute inset-0 flex items-center justify-center transition-opacity duration-300 cursor-pointer";
  videoContainer.appendChild(overlay);

  playButton.innerHTML = isTutorial ? tutorialPlayButton : playSvgButton;
  overlay.appendChild(playButton);

  videoContainer.addEventListener("click", (event) => {
    event.preventDefault();
    event.stopPropagation();

    const videoId = videoElem.id;
    if (videoId) {
      if (videoElem.paused) {
        playingMediaElem?.pause();
        videoElem.play().catch((err) => console.error("Play failed:", err));
        playingMediaElem = videoElem;
      } else {
        videoElem.pause();
      }
    }
  });

  videoElem.addEventListener("play", () => {
    videoElem.classList.add("z-[2]");
    overlay.classList.add("opacity-0");
    overlay.classList.add("-z-[1]");
  });

  videoElem.addEventListener("pause", () => {
    overlay.classList.remove("opacity-0");
    overlay.classList.remove("-z-[1]");
  });

  videoElem.addEventListener("ended", () => {
    videoElem.currentTime = 0; // Reset to start for replay
    overlay.classList.remove("opacity-0");
    overlay.classList.remove("-z-[1]");
  });

  if (autoPlay) {
    videoElem.muted = false;
    videoElem.play().catch(() => console.log("Autoplay blocked"));
  }
}

function initVideoPlayer(videoContainer?: HTMLElement) {
  if (videoContainer) {
    initVideoPlayerSingle(videoContainer);
  } else {
    const videoContainers = document.querySelectorAll(".video-container") as NodeListOf<HTMLElement>;
    if (videoContainers) {
      videoContainers.forEach((videoContainer) => initVideoPlayerSingle(videoContainer));
    }
  }
}

/* --- Audio Player --- */
const playSvg: string = `
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="white"
    stroke="white"
    stroke-width="2"
    stroke-linecap="round"
    stroke-linejoin="round"
    class="lucide lucide-play size-10 group-[.is-list]:size-5">
    <polygon points="6 3 20 12 6 21 6 3" />
  </svg>
`;

const pauseSvg: string = `
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="white"
    stroke="white"
    stroke-width="2"
    stroke-linecap="round"
    stroke-linejoin="round"
    class="lucide lucide-play size-10 group-[.is-list]:size-5">
    <rect x="14" y="4" width="4" height="16" rx="1"/>
    <rect x="6" y="4" width="4" height="16" rx="1"/>
  </svg>
`;

function initAudioPlayerSingle(audioContainer: HTMLElement, autoPlay: boolean = false) {
  const playAudioBtn = audioContainer.querySelector(".play-audio-btn") as HTMLElement;
  const audioElem = audioContainer.querySelector("audio") as HTMLAudioElement;

  if (!playAudioBtn || !audioElem) {
    console.error("Can't find the audio play button and audio element inside audio-container");
    return;
  }

  audioElem.controls = true; // Always enable default controls

  if (isSafari()) {
    // In Safari, rely on default controls and skip custom logic
    if (autoPlay) {
      audioElem.muted = false;
      // audioElem.play().catch(() => console.log("Autoplay blocked in Safari"));
    }
    return;
  }

  // Custom logic for non-Safari browsers
  playAudioBtn.innerHTML = playSvg;

  audioContainer.addEventListener("click", (event) => {
    event.preventDefault();
    event.stopPropagation();

    const audioId = audioElem.id;
    if (audioId) {
      if (audioElem.paused) {
        playingMediaElem?.pause();
        audioElem.play().catch((err) => console.error("Play failed:", err));
        playingMediaElem = audioElem;
      } else {
        audioElem.pause();
      }
    }
  });

  audioElem.addEventListener("play", () => {
    playAudioBtn.innerHTML = pauseSvg;
    if (audioElem.controls) {
      audioElem.classList.remove("opacity-0");
      audioElem.classList.add("opacity-70");
      audioElem.classList.add("z-[2]");
    }
  });

  audioElem.addEventListener("pause", () => {
    playAudioBtn.innerHTML = playSvg;
  });

  audioElem.addEventListener("ended", () => {
    playAudioBtn.innerHTML = playSvg;
    audioElem.currentTime = 0; // Reset to start for replay
  });

  if (autoPlay) {
    audioElem.muted = false;
    audioElem.play().catch(() => console.log("Autoplay blocked"));
  }
}

function initAudioPlayer(audioContainer?: HTMLElement) {
  if (audioContainer) {
    initAudioPlayerSingle(audioContainer);
  } else {
    const audioContainers = document.querySelectorAll(".audio-container") as NodeListOf<HTMLElement>;
    if (audioContainers) {
      audioContainers.forEach((audioContainer) => initAudioPlayerSingle(audioContainer));
    }
  }
}

export { initAudioPlayer, initAudioPlayerSingle, initVideoPlayer, initVideoPlayerSingle };